import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../config/firebase';
import { collection, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { Search, Edit2, X, Save, Users as UsersIcon, Zap, Trash2, CheckSquare, Square, ChevronUp, ChevronDown } from 'lucide-react';

interface UserManagementProps {
  selectedUserId?: string | null;
  onUserSelected?: () => void;
}

interface UserData {
  id: string;
  name: string;
  coins: number;
  bombHints: number;
  letterHints: number;
  magicHints: number;
  currentLevel: number;
  lastUpdated: string;
}

interface EditModalProps {
  user: UserData | null;
  onClose: () => void;
  onSave: (userId: string, data: Partial<UserData>) => Promise<void>;
}

const EditUserModal: React.FC<EditModalProps> = ({ user, onClose, onSave }) => {
  const [formData, setFormData] = useState<Partial<UserData>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name,
        coins: user.coins,
        bombHints: user.bombHints,
        letterHints: user.letterHints,
        magicHints: user.magicHints,
        currentLevel: user.currentLevel,
      });
    }
  }, [user]);

  if (!user) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await onSave(user.id, {
        ...formData,
        lastUpdated: new Date().toISOString().slice(0, 19).replace('T', ' ')
      });
      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      setError(err.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '12px',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: 0 }}>Edit User</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#6b7280',
            }}
          >
            <X size={24} />
          </button>
        </div>

        {error && (
          <div style={{
            background: '#fee2e2',
            color: '#dc2626',
            padding: '0.75rem',
            borderRadius: '6px',
            marginBottom: '1rem',
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            background: '#d1fae5',
            color: '#065f46',
            padding: '0.75rem',
            borderRadius: '6px',
            marginBottom: '1rem',
          }}>
            User updated successfully!
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
              Name
            </label>
            <input
              type="text"
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              maxLength={100}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '1rem',
              }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
              Coins
            </label>
            <input
              type="number"
              value={formData.coins ?? 0}
              onChange={(e) => setFormData({ ...formData, coins: parseInt(e.target.value) })}
              required
              min={0}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '1rem',
              }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
              Bomb Hints
            </label>
            <input
              type="number"
              value={formData.bombHints ?? 0}
              onChange={(e) => setFormData({ ...formData, bombHints: parseInt(e.target.value) })}
              required
              min={0}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '1rem',
              }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
              Letter Hints
            </label>
            <input
              type="number"
              value={formData.letterHints ?? 0}
              onChange={(e) => setFormData({ ...formData, letterHints: parseInt(e.target.value) })}
              required
              min={0}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '1rem',
              }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
              Magic Hints
            </label>
            <input
              type="number"
              value={formData.magicHints ?? 0}
              onChange={(e) => setFormData({ ...formData, magicHints: parseInt(e.target.value) })}
              required
              min={0}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '1rem',
              }}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', fontWeight: '500', marginBottom: '0.5rem' }}>
              Current Level
            </label>
            <input
              type="number"
              value={formData.currentLevel ?? 0}
              onChange={(e) => setFormData({ ...formData, currentLevel: parseInt(e.target.value) })}
              required
              min={0}
              max={10000}
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '1rem',
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '0.5rem 1rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                background: 'white',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: '0.5rem 1rem',
                border: 'none',
                borderRadius: '6px',
                background: '#3b82f6',
                color: 'white',
                cursor: saving ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <Save size={16} />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface DeleteModalProps {
  user: UserData | null;
  onClose: () => void;
  onConfirm: (userId: string) => Promise<void>;
}

const DeleteConfirmationModal: React.FC<DeleteModalProps> = ({ user, onClose, onConfirm }) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user) return null;

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);

    try {
      await onConfirm(user.id);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '12px',
          maxWidth: '500px',
          width: '90%',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: 0, color: '#dc2626' }}>Delete User</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
            }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{
          backgroundColor: '#fee2e2',
          border: '2px solid #dc2626',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1.5rem'
        }}>
          <p style={{ margin: '0 0 0.5rem 0', fontWeight: 'bold', color: '#991b1b' }}>
            ⚠️ WARNING: This action cannot be undone!
          </p>
          <p style={{ margin: 0, color: '#7f1d1d' }}>
            You are about to permanently delete the user <strong>{user.name}</strong> (ID: {user.id}).
            All their progress, coins, and hints will be lost forever.
          </p>
        </div>

        <p style={{ marginBottom: '1.5rem', fontSize: '1rem', color: '#374151' }}>
          Are you sure you want to delete this user?
        </p>

        {error && (
          <div style={{
            backgroundColor: '#fee2e2',
            color: '#991b1b',
            padding: '0.75rem',
            borderRadius: '6px',
            marginBottom: '1rem',
            fontSize: '0.875rem'
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#f3f4f6',
              border: 'none',
              borderRadius: '6px',
              cursor: deleting ? 'not-allowed' : 'pointer',
              fontWeight: '500',
              opacity: deleting ? 0.5 : 1,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: deleting ? 'not-allowed' : 'pointer',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              opacity: deleting ? 0.5 : 1,
            }}
          >
            <Trash2 size={16} />
            {deleting ? 'Deleting...' : 'Yes, Delete User'}
          </button>
        </div>
      </div>
    </div>
  );
};

const UserManagement: React.FC<UserManagementProps> = ({ selectedUserId, onUserSelected }) => {
  const [users, setUsers] = useState<UserData[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState<UserData | null>(null);
  const [deletingUser, setDeletingUser] = useState<UserData | null>(null);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());

  type SortField = 'name' | 'currentLevel' | 'coins' | 'bombHints' | 'letterHints' | 'magicHints' | 'lastUpdated';
  const [sortField, setSortField] = useState<SortField>('lastUpdated');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const usersSnapshot = await getDocs(collection(db, 'Users'));
      const usersData: UserData[] = [];

      usersSnapshot.forEach((doc) => {
        usersData.push({
          id: doc.id,
          ...doc.data() as Omit<UserData, 'id'>
        });
      });

      // Sort by last updated (most recent first)
      usersData.sort((a, b) => {
        const dateA = new Date(a.lastUpdated || 0);
        const dateB = new Date(b.lastUpdated || 0);
        return dateB.getTime() - dateA.getTime();
      });

      setUsers(usersData);
    } catch (err: any) {
      setError(err.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = useCallback(() => {
    if (!searchTerm.trim()) {
      setFilteredUsers(users);
      return;
    }

    const search = searchTerm.toLowerCase();
    let filtered = users.filter(user =>
      user.name?.toLowerCase().includes(search) ||
      user.id.toLowerCase().includes(search)
    );

    filtered.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === 'name') {
        aVal = (aVal as string)?.toLowerCase() || '';
        bVal = (bVal as string)?.toLowerCase() || '';
      } else if (sortField === 'lastUpdated') {
        aVal = new Date((aVal as string) || 0).getTime();
        bVal = new Date((bVal as string) || 0).getTime();
      } else {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredUsers(filtered);
  }, [users, searchTerm, sortField, sortDirection]);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, sortField, sortDirection, filterUsers]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Auto-open edit modal when selectedUserId is provided
  useEffect(() => {
    if (selectedUserId && users.length > 0) {
      const user = users.find(u => u.id === selectedUserId);
      if (user) {
        setEditingUser(user);
        if (onUserSelected) {
          onUserSelected();
        }
      }
    }
  }, [selectedUserId, users, onUserSelected]);

  const handleSaveUser = async (userId: string, data: Partial<UserData>) => {
    const userRef = doc(db, 'Users', userId);
    await updateDoc(userRef, data);

    // Update local state
    setUsers(users.map(u =>
      u.id === userId ? { ...u, ...data } as UserData : u
    ));
  };

  const handleDeleteUser = async (userId: string) => {
    const userRef = doc(db, 'Users', userId);
    await deleteDoc(userRef);

    // Update local state
    setUsers(users.filter(u => u.id !== userId));
    setDeletingUser(null);
  };

  const toggleUserSelection = (userId: string) => {
    const newSelection = new Set(selectedUserIds);
    if (newSelection.has(userId)) {
      newSelection.delete(userId);
    } else {
      newSelection.add(userId);
    }
    setSelectedUserIds(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedUserIds.size === filteredUsers.length && filteredUsers.length > 0) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(filteredUsers.map(u => u.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedUserIds.size === 0) return;

    if (!window.confirm(`Are you sure you want to delete ${selectedUserIds.size} users? This action cannot be undone!`)) {
      return;
    }

    try {
      // Delete all selected users
      const deletePromises = Array.from(selectedUserIds).map(userId =>
        deleteDoc(doc(db, 'Users', userId))
      );
      await Promise.all(deletePromises);

      // Update local state
      setUsers(users.filter(u => !selectedUserIds.has(u.id)));
      setSelectedUserIds(new Set());
    } catch (err: any) {
      alert('Failed to delete users: ' + (err.message || 'Unknown error'));
    }
  };

  const getStats = () => {
    const totalUsers = users.length;
    const avgLevel = users.length > 0
      ? (users.reduce((sum, u) => sum + (u.currentLevel || 0), 0) / users.length).toFixed(1)
      : '0';
    const highestLevel = users.length > 0
      ? Math.max(...users.map(u => u.currentLevel || 0))
      : 0;

    return { totalUsers, avgLevel, highestLevel };
  };

  const stats = getStats();

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <div className="loader" style={{ margin: '0 auto' }}></div>
        <p style={{ marginTop: '1rem', color: '#6b7280' }}>Loading users...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <h2>User Management</h2>
        <p style={{ color: '#6b7280', marginTop: '0.5rem' }}>
          View and edit player data
        </p>
      </div>

      {/* Stats Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <UsersIcon size={20} />
            <h3 style={{ margin: 0, fontSize: '0.875rem', opacity: 0.9 }}>Total Users</h3>
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{stats.totalUsers}</div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <Zap size={20} />
            <h3 style={{ margin: 0, fontSize: '0.875rem', opacity: 0.9 }}>Highest Level</h3>
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{stats.highestLevel}</div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          color: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <Zap size={20} />
            <h3 style={{ margin: 0, fontSize: '0.875rem', opacity: 0.9 }}>Average Level</h3>
          </div>
          <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{stats.avgLevel}</div>
        </div>
      </div>

      {error && (
        <div style={{
          background: '#fee2e2',
          color: '#dc2626',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1rem',
        }}>
          {error}
        </div>
      )}

      {/* Bulk Actions Toolbar */}
      {selectedUserIds.size > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <CheckSquare size={20} />
            <span style={{ fontWeight: 500 }}>
              {selectedUserIds.size} user{selectedUserIds.size !== 1 ? 's' : ''} selected
            </span>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={handleBulkDelete}
              style={{
                padding: '0.5rem 1rem',
                background: '#dc2626',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontWeight: 500,
              }}
            >
              <Trash2 size={16} />
              Delete Selected
            </button>
            <button
              onClick={() => setSelectedUserIds(new Set())}
              style={{
                padding: '0.5rem 1rem',
                background: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 500,
              }}
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div style={{ marginBottom: '1.5rem', position: 'relative' }}>
        <Search size={18} style={{
          position: 'absolute',
          left: '0.75rem',
          top: '50%',
          transform: 'translateY(-50%)',
          color: '#9ca3af'
        }} />
        <input
          type="text"
          placeholder="Search users by name or ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            width: '100%',
            padding: '0.75rem 0.75rem 0.75rem 2.5rem',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '1rem',
          }}
        />
      </div>

      {/* Users Table */}
      <div style={{
        background: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <th style={{ padding: '1rem', textAlign: 'center', width: '50px' }}>
                <button
                  onClick={toggleSelectAll}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0.25rem',
                  }}
                  title={selectedUserIds.size === filteredUsers.length && filteredUsers.length > 0 ? 'Deselect All' : 'Select All'}
                >
                  {selectedUserIds.size === filteredUsers.length && filteredUsers.length > 0 ? (
                    <CheckSquare size={20} color="#3b82f6" />
                  ) : (
                    <Square size={20} color="#6b7280" />
                  )}
                </button>
              </th>
              {[
                { field: 'name', label: 'Name', align: 'left' },
                { field: 'currentLevel', label: 'Level', align: 'right' },
                { field: 'coins', label: 'Coins', align: 'right' },
                { field: 'bombHints', label: 'Bomb', align: 'right' },
                { field: 'letterHints', label: 'Letter', align: 'right' },
                { field: 'magicHints', label: 'Magic', align: 'right' },
                { field: 'lastUpdated', label: 'Last Updated', align: 'left' }
              ].map(({ field, label, align }) => (
                <th
                  key={field}
                  onClick={() => handleSort(field as SortField)}
                  style={{
                    padding: '1rem',
                    textAlign: align as any,
                    fontWeight: 600,
                    color: '#374151',
                    cursor: 'pointer',
                    userSelect: 'none'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: align === 'right' ? 'flex-end' : 'flex-start', gap: '0.25rem' }}>
                    {label}
                    {sortField === field && (
                      sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                    )}
                  </div>
                </th>
              ))}
              <th style={{ padding: '1rem', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                  No users found
                </td>
              </tr>
            ) : (
              filteredUsers.map((user) => (
                <tr key={user.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    <button
                      onClick={() => toggleUserSelection(user.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0.25rem',
                      }}
                    >
                      {selectedUserIds.has(user.id) ? (
                        <CheckSquare size={20} color="#3b82f6" />
                      ) : (
                        <Square size={20} color="#6b7280" />
                      )}
                    </button>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div>
                      <div style={{ fontWeight: 500 }}>{user.name || 'Unknown'}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', fontFamily: 'monospace' }}>
                        {user.id.substring(0, 8)}...
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'right' }}>
                    <span style={{
                      background: '#dbeafe',
                      color: '#1e40af',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    }}>
                      {user.currentLevel || 0}
                    </span>
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'right', fontWeight: 500 }}>{user.coins || 0}</td>
                  <td style={{ padding: '1rem', textAlign: 'right' }}>{user.bombHints || 0}</td>
                  <td style={{ padding: '1rem', textAlign: 'right' }}>{user.letterHints || 0}</td>
                  <td style={{ padding: '1rem', textAlign: 'right' }}>{user.magicHints || 0}</td>
                  <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                    {user.lastUpdated ? new Date(user.lastUpdated).toLocaleString() : '-'}
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                      <button
                        onClick={() => setEditingUser(user)}
                        style={{
                          padding: '0.5rem 1rem',
                          background: '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                        }}
                      >
                        <Edit2 size={14} />
                        Edit
                      </button>
                      <button
                        onClick={() => setDeletingUser(user)}
                        style={{
                          padding: '0.5rem 1rem',
                          background: '#dc2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                        }}
                      >
                        <Trash2 size={14} />
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={handleSaveUser}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingUser && (
        <DeleteConfirmationModal
          user={deletingUser}
          onClose={() => setDeletingUser(null)}
          onConfirm={handleDeleteUser}
        />
      )}
    </div>
  );
};

export default UserManagement;
